/**
 * useGitHubRoadmap — GitHub Issues Live-Sync für die Kommandozentrale
 * 
 * V2.0 — KEIN Cache-Tabelle nötig!
 * 
 * Datenquellen:
 * 1. GitHub API (über /api/github-sync Proxy) → Live Issues
 * 2. agenten_vorschlaege (Supabase) → Feedback-Daten
 * 
 * Workflow:
 * 1. Manus erstellt Issues mit Research + Tipps (agent:manus)
 * 2. Claude Code übernimmt Implementation (agent:claude-code)
 * 3. User gibt Feedback → agenten_vorschlaege + optional GitHub Comment
 * 4. Manus-Dev-Agent prüft Umsetzung und gibt weitere Tipps
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import {
  mapLabelsToStatus,
  mapLabelsToPrioritaet,
  mapLabelsToTyp,
  mapLabelsToAgent,
} from './roadmap-label-utils';
import type {
  GitHubLabel,
  RoadmapStatus,
  RoadmapPrioritaet,
  RoadmapTyp,
} from './roadmap-label-utils';

// Re-export types and functions for consumers
export { mapLabelsToStatus, mapLabelsToAgent };
export type { GitHubLabel, RoadmapStatus, RoadmapPrioritaet, RoadmapTyp };

// ═══════════════════════════════════════════════════════════════════════════
// TYPEN
// ═══════════════════════════════════════════════════════════════════════════

export interface GitHubProject {
  id: string;
  repo: string;
  name: string;
  icon: string;
  color: string;
  beschreibung: string;
}

export interface GitHubIssue {
  id: string;
  number: number;
  title: string;
  body: string;
  state: 'open' | 'closed';
  html_url: string;
  repo: string;
  projekt: string;
  labels: GitHubLabel[];
  created_at: string;
  updated_at: string;
  status: RoadmapStatus;
  prioritaet: RoadmapPrioritaet;
  typ: RoadmapTyp;
  agent: 'manus' | 'claude-code' | 'unassigned';
  feedbacks: IssueFeedback[];
  checkliste: ChecklistItem[];
  fortschritt: number;
}

export interface IssueFeedback {
  id: string;
  text: string;
  screenshot_url?: string;
  erstellt_am: string;
  erstellt_von: string;
  typ: 'feedback' | 'tipp' | 'bug' | 'verbesserung';
}

export interface ChecklistItem {
  text: string;
  checked: boolean;
}

export interface GitHubKommentar {
  id: number;
  body: string;
  user: string;
  created_at: string;
  is_claude: boolean;
  is_antwort: boolean;
}

// ═══════════════════════════════════════════════════════════════════════════
// PROJEKTE-KONFIGURATION
// ═══════════════════════════════════════════════════════════════════════════

export const GITHUB_PROJEKTE: GitHubProject[] = [
  {
    id: 'gutachter-koop-app',
    repo: 'AndKo88/gutachter-koop-app',
    name: 'Gutachter Koop App',
    icon: 'Rocket',
    color: '#C41E3A',
    beschreibung: 'Internes Verwaltungssystem — React + Supabase',
  },
  {
    id: 'unfallgiganten',
    repo: 'AndKo88/unfallgiganten',
    name: 'Unfallgiganten',
    icon: 'Car',
    color: '#2563EB',
    beschreibung: 'Kunden-Plattform — Next.js',
  },
  {
    id: 'flooas',
    repo: 'AndKo88/flooas',
    name: 'Flooas',
    icon: 'Globe',
    color: '#7C3AED',
    beschreibung: 'Suchplattform — Laravel + Vue',
  },
  {
    id: 'winvalue-automation',
    repo: 'AndKo88/winvalue-automation',
    name: 'WinValue Automation',
    icon: 'FileSearch',
    color: '#059669',
    beschreibung: 'Python Pipeline — WinValue + Rentsoft',
  },
  {
    id: 'gutachter-koop-website',
    repo: 'AndKo88/gutachter-koop-website',
    name: 'Gutachter Koop Website',
    icon: 'Globe',
    color: '#D97706',
    beschreibung: 'Öffentliche Website — Next.js 14',
  },
  {
    id: 'gutachter-koop-mcp',
    repo: 'AndKo88/gutachter-koop-mcp',
    name: 'MCP Server',
    icon: 'Server',
    color: '#0891B2',
    beschreibung: 'MCP Server — 26 Supabase-Tools',
  },
  {
    id: 'lenneglas',
    repo: 'AndKo88/Lenneglas',
    name: 'Lenneglas',
    icon: 'Diamond',
    color: '#DB2777',
    beschreibung: 'Autoglas-Service Website',
  },
  {
    id: 'mietgo',
    repo: 'AndKo88/mietgo',
    name: 'MietGo',
    icon: 'Key',
    color: '#EA580C',
    beschreibung: 'Mietwagen-Plattform One-Pager — mietgo.de',
  },
];

// ═══════════════════════════════════════════════════════════════════════════
// CHECKLIST PARSING
// ═══════════════════════════════════════════════════════════════════════════

function extractChecklist(body: string): ChecklistItem[] {
  if (!body) return [];
  const items: ChecklistItem[] = [];
  const regex = /- \[([ xX])\] (.+)/g;
  let match;
  while ((match = regex.exec(body)) !== null) {
    items.push({
      checked: match[1] !== ' ',
      text: match[2].trim(),
    });
  }
  return items;
}

function mapRawToIssue(raw: any, repo: string): GitHubIssue {
  const labels = raw.labels || [];
  const checkliste = extractChecklist(raw.body || '');
  const checked = checkliste.filter((c: ChecklistItem) => c.checked).length;
  const total = checkliste.length;
  const projekt = GITHUB_PROJEKTE.find(p => p.repo === repo);

  return {
    id: raw.id || `${repo}#${raw.number}`,
    number: raw.number,
    title: raw.title,
    body: raw.body || '',
    state: raw.state as 'open' | 'closed',
    html_url: raw.html_url,
    repo,
    projekt: projekt?.id || 'unknown',
    labels,
    created_at: raw.created_at,
    updated_at: raw.updated_at,
    status: raw.state === 'closed' ? 'fertig' : mapLabelsToStatus(labels),
    prioritaet: mapLabelsToPrioritaet(labels),
    typ: mapLabelsToTyp(labels),
    agent: mapLabelsToAgent(labels),
    feedbacks: [],
    checkliste,
    fortschritt: total > 0 ? Math.round((checked / total) * 100) : 0,
  };
}

async function githubSyncFetch(body: object): Promise<Response> {
  const { data: { session } } = await supabase.auth.getSession();
  return fetch('/api/github-sync', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}),
    },
    body: JSON.stringify(body),
  });
}

// ═══════════════════════════════════════════════════════════════════════════
// HOOK
// ═══════════════════════════════════════════════════════════════════════════

export function useGitHubRoadmap() {
  const [issues, setIssues] = useState<GitHubIssue[]>([]);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [selectedProjekt, setSelectedProjekt] = useState<string>('alle');
  const [selectedStatus, setSelectedStatus] = useState<string>('alle');
  const [lastSyncAt, setLastSyncAt] = useState<Date | null>(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const loadedRef = useRef(false);
  const autoRefreshRef = useRef(autoRefresh);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // ─── Feedback-Daten aus agenten_vorschlaege laden ────────────────────
  const loadFeedbacksFromSupabase = useCallback(async (): Promise<Map<string, IssueFeedback[]>> => {
    const feedbackMap = new Map<string, IssueFeedback[]>();
    try {
      const { data } = await supabase
        .from('agenten_vorschlaege')
        .select('*')
        .eq('vorschlag_typ', 'feedback')
        .order('erstellt_am', { ascending: false });

      if (data) {
        for (const row of data) {
          const kontext = row.kontext || row.meta || {};
          const issueKey = kontext.github_repo && kontext.github_issue
            ? `${kontext.github_repo}#${kontext.github_issue}`
            : null;

          if (issueKey) {
            const existing = feedbackMap.get(issueKey) || [];
            existing.push({
              id: row.id,
              text: row.beschreibung || row.vorschlag_text || '',
              screenshot_url: kontext.screenshot_url,
              erstellt_am: row.erstellt_am,
              erstellt_von: 'admin',
              typ: kontext.feedback_typ || 'feedback',
            });
            feedbackMap.set(issueKey, existing);
          }
        }
      }
    } catch (err) {
      console.error('Feedback laden:', err);
    }
    return feedbackMap;
  }, []);

  // ─── Issues über API laden ──────────────────────────────────────────
  const loadFromAPI = useCallback(async (): Promise<GitHubIssue[]> => {
    try {
      const response = await githubSyncFetch({ action: 'sync_all' });

      if (!response.ok) {
        throw new Error(`API ${response.status}`);
      }

      const data = await response.json();
      if (data.issues && data.issues.length > 0) {
        return data.issues.map((raw: any) => mapRawToIssue(raw, raw.repo));
      }
      return [];
    } catch (err) {
      console.error('GitHub API Fehler:', err);
      return [];
    }
  }, []);

  // ─── Feedbacks an Issues anhängen ───────────────────────────────────
  const mergeFeeedbacks = useCallback((issues: GitHubIssue[], feedbackMap: Map<string, IssueFeedback[]>): GitHubIssue[] => {
    return issues.map(issue => {
      const key = `${issue.repo}#${issue.number}`;
      const feedbacks = feedbackMap.get(key) || [];
      return { ...issue, feedbacks };
    });
  }, []);

  // ─── GitHub Sync ────────────────────────────────────────────────────
  const syncFromGitHub = useCallback(async (silent = false) => {
    setSyncing(true);
    try {
      const [apiIssues, feedbackMap] = await Promise.all([
        loadFromAPI(),
        loadFeedbacksFromSupabase(),
      ]);

      if (apiIssues.length > 0) {
        const merged = mergeFeeedbacks(apiIssues, feedbackMap);
        setIssues(merged);
        setLastSyncAt(new Date());
        if (!silent) toast.success(`${merged.length} Issues von GitHub geladen`);
      } else {
        if (!silent) toast.info('Keine Issues von GitHub — API-Route noch nicht konfiguriert. Nutze lokale Daten.');
        // Fallback: Lokale Seed-Daten laden
        await loadSeedData(feedbackMap);
        setLastSyncAt(new Date());
      }
    } catch (err) {
      console.error('Sync Fehler:', err);
      if (!silent) toast.error('Sync fehlgeschlagen');
    } finally {
      setSyncing(false);
    }
  }, [loadFromAPI, loadFeedbacksFromSupabase, mergeFeeedbacks]);

  // ─── Seed-Daten als Fallback (aus agenten_vorschlaege + bekannte Issues) ──
  const loadSeedData = useCallback(async (feedbackMap?: Map<string, IssueFeedback[]>) => {
    const fbMap = feedbackMap || await loadFeedbacksFromSupabase();
    
    // Bekannte Issues aus den GitHub-Repos (Seed)
    const seedIssues: GitHubIssue[] = [
      // gutachter-koop-app
      { id: 'gka#4', number: 4, title: 'Agenten-System V5 Upgrade', body: '', state: 'closed', html_url: 'https://github.com/AndKo88/gutachter-koop-app/issues/4', repo: 'AndKo88/gutachter-koop-app', projekt: 'gutachter-koop-app', labels: [{name:'type:feature',color:'a2eeef'},{name:'priority:critical',color:'b60205'},{name:'agent:manus',color:'7C3AED'}], created_at: '2026-04-13T10:00:00Z', updated_at: '2026-04-13T14:00:00Z', status: 'fertig', prioritaet: 'critical', typ: 'feature', agent: 'manus', feedbacks: [], checkliste: [], fortschritt: 100 },
      { id: 'gka#9', number: 9, title: 'Akten-Prozessschritte mit Formularen', body: '- [ ] Auftragsformular\n- [ ] Fahrzeugdaten\n- [ ] Lichtbilder\n- [ ] Kalkulation\n- [ ] Gutachten-PDF', state: 'open', html_url: 'https://github.com/AndKo88/gutachter-koop-app/issues/9', repo: 'AndKo88/gutachter-koop-app', projekt: 'gutachter-koop-app', labels: [{name:'type:feature',color:'a2eeef'},{name:'priority:critical',color:'b60205'},{name:'status:in-progress',color:'fbca04'},{name:'agent:claude-code',color:'2563EB'}], created_at: '2026-04-13T14:00:00Z', updated_at: '2026-04-13T14:00:00Z', status: 'in_arbeit', prioritaet: 'critical', typ: 'feature', agent: 'claude-code', feedbacks: [], checkliste: [{text:'Auftragsformular',checked:false},{text:'Fahrzeugdaten',checked:false},{text:'Lichtbilder',checked:false},{text:'Kalkulation',checked:false},{text:'Gutachten-PDF',checked:false}], fortschritt: 0 },
      { id: 'gka#10', number: 10, title: '2FA Authentifizierung', body: '', state: 'open', html_url: 'https://github.com/AndKo88/gutachter-koop-app/issues/10', repo: 'AndKo88/gutachter-koop-app', projekt: 'gutachter-koop-app', labels: [{name:'type:feature',color:'a2eeef'},{name:'priority:high',color:'d93f0b'},{name:'agent:manus',color:'7C3AED'}], created_at: '2026-04-13T14:00:00Z', updated_at: '2026-04-13T14:00:00Z', status: 'idee', prioritaet: 'high', typ: 'feature', agent: 'manus', feedbacks: [], checkliste: [], fortschritt: 0 },
      { id: 'gka#11', number: 11, title: 'Stellungnahmen-Modul V5.1', body: '', state: 'open', html_url: 'https://github.com/AndKo88/gutachter-koop-app/issues/11', repo: 'AndKo88/gutachter-koop-app', projekt: 'gutachter-koop-app', labels: [{name:'type:feature',color:'a2eeef'},{name:'priority:high',color:'d93f0b'},{name:'status:in-progress',color:'fbca04'},{name:'agent:claude-code',color:'2563EB'}], created_at: '2026-04-13T14:00:00Z', updated_at: '2026-04-13T14:00:00Z', status: 'in_arbeit', prioritaet: 'high', typ: 'feature', agent: 'claude-code', feedbacks: [], checkliste: [], fortschritt: 0 },
      { id: 'gka#12', number: 12, title: 'GitHub-Roadmap-Sync in Kommandozentrale', body: '', state: 'closed', html_url: 'https://github.com/AndKo88/gutachter-koop-app/issues/12', repo: 'AndKo88/gutachter-koop-app', projekt: 'gutachter-koop-app', labels: [{name:'type:feature',color:'a2eeef'},{name:'priority:critical',color:'b60205'},{name:'status:review',color:'7C3AED'},{name:'agent:manus',color:'7C3AED'}], created_at: '2026-04-13T14:00:00Z', updated_at: '2026-04-13T16:00:00Z', status: 'fertig', prioritaet: 'critical', typ: 'feature', agent: 'manus', feedbacks: [], checkliste: [], fortschritt: 100 },
      // unfallgiganten
      { id: 'ug#397', number: 397, title: 'Sofortakte Quick-Start', body: '', state: 'open', html_url: 'https://github.com/simplefoxGmbH/unfallgiganten/issues/397', repo: 'simplefoxGmbH/unfallgiganten', projekt: 'unfallgiganten', labels: [{name:'type:feature',color:'a2eeef'},{name:'priority:critical',color:'b60205'},{name:'agent:manus',color:'7C3AED'}], created_at: '2026-04-13T14:00:00Z', updated_at: '2026-04-13T14:00:00Z', status: 'idee', prioritaet: 'critical', typ: 'feature', agent: 'manus', feedbacks: [], checkliste: [], fortschritt: 0 },
      { id: 'ug#398', number: 398, title: 'Anbieter-Dashboard 1:1 Rebuild', body: '', state: 'open', html_url: 'https://github.com/simplefoxGmbH/unfallgiganten/issues/398', repo: 'simplefoxGmbH/unfallgiganten', projekt: 'unfallgiganten', labels: [{name:'type:feature',color:'a2eeef'},{name:'priority:critical',color:'b60205'},{name:'agent:claude-code',color:'2563EB'},{name:'status:ready',color:'0e8a16'}], created_at: '2026-04-13T14:00:00Z', updated_at: '2026-04-13T14:00:00Z', status: 'geplant', prioritaet: 'critical', typ: 'feature', agent: 'claude-code', feedbacks: [], checkliste: [], fortschritt: 0 },
      { id: 'ug#399', number: 399, title: 'Stellungnahme-Workflow für Anwälte', body: '', state: 'open', html_url: 'https://github.com/simplefoxGmbH/unfallgiganten/issues/399', repo: 'simplefoxGmbH/unfallgiganten', projekt: 'unfallgiganten', labels: [{name:'type:feature',color:'a2eeef'},{name:'priority:high',color:'d93f0b'},{name:'agent:manus',color:'7C3AED'}], created_at: '2026-04-13T14:00:00Z', updated_at: '2026-04-13T14:00:00Z', status: 'idee', prioritaet: 'high', typ: 'feature', agent: 'manus', feedbacks: [], checkliste: [], fortschritt: 0 },
      // winvalue
      { id: 'wv#2', number: 2, title: 'WinValue Daten-Import Pipeline', body: '', state: 'open', html_url: 'https://github.com/AndKo88/winvalue-automation/issues/2', repo: 'AndKo88/winvalue-automation', projekt: 'winvalue-automation', labels: [{name:'type:feature',color:'a2eeef'},{name:'priority:high',color:'d93f0b'},{name:'agent:claude-code',color:'2563EB'},{name:'status:ready',color:'0e8a16'}], created_at: '2026-04-13T14:00:00Z', updated_at: '2026-04-13T14:00:00Z', status: 'geplant', prioritaet: 'high', typ: 'feature', agent: 'claude-code', feedbacks: [], checkliste: [], fortschritt: 0 },
      // website
      { id: 'gkw#3', number: 3, title: 'Google Maps API Integration', body: '', state: 'open', html_url: 'https://github.com/AndKo88/gutachter-koop-website/issues/3', repo: 'AndKo88/gutachter-koop-website', projekt: 'gutachter-koop-website', labels: [{name:'type:bug',color:'d73a4a'},{name:'priority:high',color:'d93f0b'},{name:'agent:claude-code',color:'2563EB'},{name:'status:ready',color:'0e8a16'}], created_at: '2026-04-13T14:00:00Z', updated_at: '2026-04-13T14:00:00Z', status: 'geplant', prioritaet: 'high', typ: 'bug', agent: 'claude-code', feedbacks: [], checkliste: [], fortschritt: 0 },
      { id: 'gkw#4', number: 4, title: 'SEO-Optimierung', body: '', state: 'open', html_url: 'https://github.com/AndKo88/gutachter-koop-website/issues/4', repo: 'AndKo88/gutachter-koop-website', projekt: 'gutachter-koop-website', labels: [{name:'type:feature',color:'a2eeef'},{name:'priority:medium',color:'fbca04'},{name:'agent:manus',color:'7C3AED'}], created_at: '2026-04-13T14:00:00Z', updated_at: '2026-04-13T14:00:00Z', status: 'idee', prioritaet: 'medium', typ: 'feature', agent: 'manus', feedbacks: [], checkliste: [], fortschritt: 0 },
      // lenneglas
      { id: 'lg#2', number: 2, title: 'Lenneglas Website Launch', body: '', state: 'open', html_url: 'https://github.com/AndKo88/Lenneglas/issues/2', repo: 'AndKo88/Lenneglas', projekt: 'lenneglas', labels: [{name:'type:feature',color:'a2eeef'},{name:'priority:medium',color:'fbca04'},{name:'agent:manus',color:'7C3AED'}], created_at: '2026-04-13T14:00:00Z', updated_at: '2026-04-13T14:00:00Z', status: 'idee', prioritaet: 'medium', typ: 'feature', agent: 'manus', feedbacks: [], checkliste: [], fortschritt: 0 },
    ];

    // Feedbacks mergen
    const merged = mergeFeeedbacks(seedIssues, fbMap);
    setIssues(merged);
    return merged;
  }, [loadFeedbacksFromSupabase, mergeFeeedbacks]);

  // ─── Initial laden ──────────────────────────────────────────────────
  const loadIssues = useCallback(async () => {
    if (loadedRef.current) return;
    loadedRef.current = true;
    setLoading(true);
    try {
      const [apiIssues, feedbackMap] = await Promise.all([
        loadFromAPI(),
        loadFeedbacksFromSupabase(),
      ]);

      if (apiIssues.length > 0) {
        const merged = mergeFeeedbacks(apiIssues, feedbackMap);
        setIssues(merged);
        setLastSyncAt(new Date());
      } else {
        await loadSeedData(feedbackMap);
        setLastSyncAt(new Date());
      }
    } catch (err) {
      console.error('Issues laden:', err);
      await loadSeedData();
    } finally {
      setLoading(false);
    }
  }, [loadFromAPI, loadFeedbacksFromSupabase, mergeFeeedbacks, loadSeedData]);

  useEffect(() => {
    loadIssues();
  }, [loadIssues]);

  // ─── Auto-Polling alle 5 Minuten ──────────────────────────────────
  useEffect(() => {
    autoRefreshRef.current = autoRefresh;
  }, [autoRefresh]);

  useEffect(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    if (autoRefresh) {
      intervalRef.current = setInterval(() => {
        if (autoRefreshRef.current) {
          syncFromGitHub(true);
        }
      }, 5 * 60 * 1000);
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [autoRefresh, syncFromGitHub]);

  // ─── Feedback zu einem Issue hinzufügen ─────────────────────────────
  const addFeedback = useCallback(async (
    issueId: string,
    text: string,
    typ: IssueFeedback['typ'] = 'feedback',
    screenshotFile?: File,
  ): Promise<boolean> => {
    try {
      let screenshot_url: string | undefined;

      // Screenshot hochladen
      if (screenshotFile) {
        const fileName = `feedback/${Date.now()}_${screenshotFile.name}`;
        const { error: uploadError } = await supabase.storage
          .from('dokumente')
          .upload(fileName, screenshotFile, { contentType: screenshotFile.type });

        if (!uploadError) {
          const { data: urlData } = supabase.storage
            .from('dokumente')
            .getPublicUrl(fileName);
          screenshot_url = urlData?.publicUrl;
        } else {
          console.error('Screenshot Upload:', uploadError);
        }
      }

      const feedback: IssueFeedback = {
        id: crypto.randomUUID(),
        text,
        screenshot_url,
        erstellt_am: new Date().toISOString(),
        erstellt_von: 'admin',
        typ,
      };

      // Issue in State updaten
      const issue = issues.find(i => i.id === issueId);
      setIssues(prev => prev.map(i => {
        if (i.id === issueId) {
          return { ...i, feedbacks: [...i.feedbacks, feedback] };
        }
        return i;
      }));

      // In agenten_vorschlaege speichern
      if (issue) {
        await supabase.from('agenten_vorschlaege').insert({
          agent_name: 'Manus Dev-Agent',
          kategorie: 'entwicklung',
          titel: `${typ === 'bug' ? '🐛 Bug' : typ === 'tipp' ? '💡 Tipp' : typ === 'verbesserung' ? '🔧 Verbesserung' : '💬 Feedback'}: ${issue.title}`,
          beschreibung: text,
          vorschlag_text: `Feedback zu Issue #${issue.number} (${issue.repo}):\n\n${text}${screenshot_url ? `\n\nScreenshot: ${screenshot_url}` : ''}`,
          vorschlag_typ: 'feedback',
          prioritaet: typ === 'bug' ? 9 : typ === 'verbesserung' ? 7 : 5,
          status: 'offen',
          kontext: {
            github_issue: issue.number,
            github_repo: issue.repo,
            github_url: issue.html_url,
            feedback_typ: typ,
            screenshot_url,
          },
        });

        // Optional: GitHub-Kommentar über API
        try {
          await githubSyncFetch({
            action: 'add_comment',
            repo: issue.repo,
            issue_number: issue.number,
            comment: `### ${typ === 'bug' ? '🐛 Bug-Report' : typ === 'tipp' ? '💡 Tipp' : typ === 'verbesserung' ? '🔧 Verbesserung' : '💬 Feedback'}\n\n${text}${screenshot_url ? `\n\n![Screenshot](${screenshot_url})` : ''}\n\n---\n_Über Gutachter Koop Kommandozentrale_`,
          });
        } catch {
          // GitHub-Kommentar ist optional
        }
      }

      toast.success('Feedback gespeichert');
      return true;
    } catch (err) {
      console.error('Feedback speichern:', err);
      toast.error('Feedback speichern fehlgeschlagen');
      return false;
    }
  }, [issues]);

  // ─── Neues Issue erstellen ──────────────────────────────────────────
  const createIssueFromFeedback = useCallback(async (
    projektId: string,
    title: string,
    body: string,
    typ: RoadmapTyp = 'feature',
    prioritaet: RoadmapPrioritaet = 'medium',
    screenshotUrl?: string,
    agent: 'manus' | 'claude-code' = 'claude-code',
  ): Promise<boolean> => {
    try {
      const projekt = GITHUB_PROJEKTE.find(p => p.id === projektId);
      if (!projekt) {
        toast.error('Projekt nicht gefunden');
        return false;
      }

      let fullBody = body;
      if (screenshotUrl) {
        fullBody += `\n\n### Screenshot\n![Feedback Screenshot](${screenshotUrl})`;
      }
      fullBody += `\n\n---\n_Erstellt über Gutachter Koop Kommandozentrale_`;

      const labels = [`type:${typ}`, `priority:${prioritaet}`, `agent:${agent}`, 'status:ready'];

      // 1. Vorschlag in Supabase
      await supabase.from('agenten_vorschlaege').insert({
        agent_name: 'Manus Dev-Agent',
        kategorie: 'entwicklung',
        titel: `Neues Issue: ${title}`,
        beschreibung: `Neues GitHub Issue für ${projekt.name}`,
        vorschlag_text: `Bitte Issue in ${projekt.repo} erstellen:\n\nTitel: ${title}\n\n${fullBody}`,
        vorschlag_typ: 'github_issue',
        prioritaet: prioritaet === 'critical' ? 10 : prioritaet === 'high' ? 8 : 5,
        status: 'offen',
        kontext: {
          github_repo: projekt.repo,
          github_labels: labels,
          issue_title: title,
          issue_body: fullBody,
          aktion: 'create_github_issue',
        },
      });

      // 2. Versuche direkt in GitHub zu erstellen
      let ghIssueNumber = 0;
      let ghUrl = `https://github.com/${projekt.repo}/issues`;
      try {
        const response = await githubSyncFetch({
          action: 'create_issue',
          repo: projekt.repo,
          title,
          body: fullBody,
          labels,
        });
        if (response.ok) {
          const data = await response.json();
          ghIssueNumber = data.issue?.number || 0;
          ghUrl = data.issue?.html_url || ghUrl;
        }
      } catch {
        // GitHub-Erstellung optional — Vorschlag reicht
      }

      // 3. Lokal hinzufügen
      const newIssue: GitHubIssue = {
        id: crypto.randomUUID(),
        number: ghIssueNumber,
        title,
        body: fullBody,
        state: 'open',
        html_url: ghUrl,
        repo: projekt.repo,
        projekt: projektId,
        labels: labels.map(l => ({ name: l, color: '999999' })),
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        status: 'idee',
        prioritaet,
        typ,
        agent: 'manus',
        feedbacks: [],
        checkliste: [],
        fortschritt: 0,
      };

      setIssues(prev => [newIssue, ...prev]);
      toast.success(ghIssueNumber > 0
        ? `Issue #${ghIssueNumber} in GitHub erstellt`
        : `Issue "${title}" erstellt — wird in Vorschläge angezeigt`
      );
      return true;
    } catch (err) {
      console.error('Issue erstellen:', err);
      toast.error('Issue erstellen fehlgeschlagen');
      return false;
    }
  }, []);

  // ─── Kommentare laden ───────────────────────────────────────────────
  const fetchComments = useCallback(async (repo: string, issueNumber: number): Promise<GitHubKommentar[]> => {
    try {
      const res = await githubSyncFetch({ action: 'get_comments', repo, issue_number: issueNumber });
      if (!res.ok) return [];
      const data = await res.json();
      const raw: any[] = data.comments || [];
      let claudeQSeen = false;
      return raw.map(c => {
        const isBot = c.user?.type === 'Bot' || c.user?.login === 'github-actions[bot]';
        const isClaudeQ = c.body.startsWith('🤔 **Clarifying Questions**') || isBot;
        const isSuccess = c.body.startsWith('✅ Implementation complete');
        const isClaude = isClaudeQ || isSuccess;
        if (isClaudeQ) claudeQSeen = true;
        const isAntwort = !isClaude && claudeQSeen;
        return { id: c.id, body: c.body, user: c.user?.login || 'unknown', created_at: c.created_at, is_claude: isClaude, is_antwort: isAntwort };
      });
    } catch {
      return [];
    }
  }, []);

  // ─── Issue bearbeiten (Agent, Status, Prio, Typ, Titel) ────────────
  const updateIssue = useCallback(async (
    issueId: string,
    updates: {
      agent?: 'manus' | 'claude-code' | 'unassigned';
      status?: RoadmapStatus;
      prioritaet?: RoadmapPrioritaet;
      typ?: RoadmapTyp;
      title?: string;
    }
  ): Promise<boolean> => {
    const issue = issues.find(i => i.id === issueId);
    if (!issue) return false;

    // Reverse-Mapping: Interne Werte → GitHub Labels
    const statusLabels: Record<RoadmapStatus, string> = {
      idee: '', geplant: 'status:ready', in_arbeit: 'status:in-progress', review: 'status:review', human_feedback: 'status:human-feedback', fertig: 'status:done',
    };
    const prioLabels: Record<RoadmapPrioritaet, string> = {
      critical: 'priority:critical', high: 'priority:high', medium: 'priority:medium', low: 'priority:low',
    };
    const typLabels: Record<RoadmapTyp, string> = {
      feature: 'type:feature', bug: 'type:bug', refactor: 'type:refactor', research: 'type:research', docs: 'type:docs',
    };
    const agentLabels: Record<string, string> = {
      manus: 'agent:manus', 'claude-code': 'agent:claude-code', unassigned: '',
    };

    // Neue Labels berechnen
    let newLabels = issue.labels.map(l => l.name);

    if (updates.status !== undefined) {
      newLabels = newLabels.filter(l => !l.startsWith('status:'));
      const sl = statusLabels[updates.status];
      if (sl) newLabels.push(sl);
    }
    if (updates.prioritaet !== undefined) {
      newLabels = newLabels.filter(l => !l.startsWith('priority:'));
      newLabels.push(prioLabels[updates.prioritaet]);
    }
    if (updates.typ !== undefined) {
      newLabels = newLabels.filter(l => !l.startsWith('type:'));
      newLabels.push(typLabels[updates.typ]);
    }
    if (updates.agent !== undefined) {
      newLabels = newLabels.filter(l => !l.startsWith('agent:'));
      const al = agentLabels[updates.agent];
      if (al) newLabels.push(al);
    }

    // State berechnen (fertig → closed, sonst open)
    const newState = (updates.status === 'fertig') ? 'closed' as const : (issue.status === 'fertig' && updates.status) ? 'open' as const : undefined;

    // Optimistisches UI-Update
    setIssues(prev => prev.map(i => {
      if (i.id !== issueId) return i;
      return {
        ...i,
        ...(updates.title && { title: updates.title }),
        ...(updates.status && { status: updates.status }),
        ...(updates.prioritaet && { prioritaet: updates.prioritaet }),
        ...(updates.typ && { typ: updates.typ }),
        ...(updates.agent && { agent: updates.agent }),
        ...(newState && { state: newState }),
        labels: newLabels.map(name => ({ name, color: '999999' })),
        updated_at: new Date().toISOString(),
      };
    }));

    // GitHub API Update
    try {
      const payload: Record<string, any> = {
        action: 'update_issue',
        repo: issue.repo,
        issue_number: issue.number,
        labels: newLabels,
      };
      if (updates.title) payload.title = updates.title;
      if (newState) payload.state = newState;

      const res = await githubSyncFetch(payload);

      if (res.ok) {
        const changeDesc = Object.entries(updates)
          .filter(([, v]) => v !== undefined)
          .map(([k, v]) => `${k}: ${v}`)
          .join(', ');
        toast.success(`Issue #${issue.number} aktualisiert: ${changeDesc}`);
        return true;
      } else {
        toast.error('GitHub-Update fehlgeschlagen — lokale Änderung bleibt');
        return false;
      }
    } catch (err) {
      console.error('Issue Update:', err);
      toast.error('GitHub-Update fehlgeschlagen');
      return false;
    }
  }, [issues]);

  // ─── Antwort posten ─────────────────────────────────────────────────
  const replyToIssue = useCallback(async (issueId: string, text: string): Promise<boolean> => {
    const issue = issues.find(i => i.id === issueId);
    if (!issue || !text.trim()) return false;
    try {
      const res = await githubSyncFetch({ action: 'add_comment', repo: issue.repo, issue_number: issue.number, comment: text });
      return res.ok;
    } catch {
      return false;
    }
  }, [issues]);

  // ─── Gefilterte + sortierte Issues ──────────────────────────────────
  const filteredIssues = issues.filter(issue => {
    if (selectedProjekt !== 'alle' && issue.projekt !== selectedProjekt) return false;
    if (selectedStatus !== 'alle' && issue.status !== selectedStatus) return false;
    return true;
  });

  const prioOrder: Record<RoadmapPrioritaet, number> = { critical: 0, high: 1, medium: 2, low: 3 };
  const sortedIssues = [...filteredIssues].sort((a, b) => {
    // 1. Open issues before closed
    if (a.state !== b.state) return a.state === 'open' ? -1 : 1;
    // 2. Priority
    if (prioOrder[a.prioritaet] !== prioOrder[b.prioritaet]) {
      return prioOrder[a.prioritaet] - prioOrder[b.prioritaet];
    }
    // 3. Most recently updated
    return new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime();
  });

  // ─── Stats ──────────────────────────────────────────────────────────
  const stats = {
    gesamt: issues.length,
    ideen: issues.filter(i => i.status === 'idee').length,
    geplant: issues.filter(i => i.status === 'geplant').length,
    inArbeit: issues.filter(i => i.status === 'in_arbeit').length,
    review: issues.filter(i => i.status === 'review').length,
    humanFeedback: issues.filter(i => i.status === 'human_feedback').length,
    fertig: issues.filter(i => i.status === 'fertig').length,
    proProjekt: GITHUB_PROJEKTE.map(p => ({
      ...p,
      count: issues.filter(i => i.projekt === p.id).length,
      open: issues.filter(i => i.projekt === p.id && i.state === 'open').length,
    })),
  };

  return {
    issues: sortedIssues,
    allIssues: issues,
    stats,
    loading,
    syncing,
    lastSyncAt,
    autoRefresh,
    setAutoRefresh,
    selectedProjekt,
    selectedStatus,
    setSelectedProjekt,
    setSelectedStatus,
    syncFromGitHub,
    addFeedback,
    createIssueFromFeedback,
    fetchComments,
    replyToIssue,
    updateIssue,
    reload: () => { loadedRef.current = false; loadIssues(); },
    PROJEKTE: GITHUB_PROJEKTE,
  };
}
